/**
 * Maps an altitude (feet, MSL) to one of the gradient stops shared with the
 * legend bar at the bottom of the map. Keep these breakpoints in sync with
 * `--trail-grad` in src/styles/tokens.css and the tick labels in
 * src/ui/legend.ts.
 *
 * Range:  0 ft → grey, 12k → amber, 22k → orange, 30k+ → purple.
 *
 * Used by:
 * - Trail rendering: `["interpolate", ["linear"], ["get","alt"], …STOPS…]`
 * - Tests: assert that boundary values resolve to the right hue.
 */

export type RgbTriplet = [number, number, number];

export const ALTITUDE_STOPS: Array<{ ft: number; rgb: RgbTriplet }> = [
  { ft: 0, rgb: [153, 153, 153] }, // grey (ground / pattern)
  { ft: 3000, rgb: [153, 153, 153] }, // grey (low approach)
  { ft: 12000, rgb: [228, 161, 28] }, // amber (mid)
  { ft: 22000, rgb: [255, 94, 0] }, // orange (climb / descent)
  { ft: 30000, rgb: [132, 27, 192] }, // purple (cruise)
];

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

export function altitudeToColor(altFt: number): string {
  // NaN → ground colour (treat as "no data"). +Infinity should be treated
  // as "above all stops" so it pins to the top stop, hence the explicit
  // NaN check before the finite-clamp at the bottom.
  if (Number.isNaN(altFt) || altFt <= ALTITUDE_STOPS[0]!.ft) {
    const [r, g, b] = ALTITUDE_STOPS[0]!.rgb;
    return `rgb(${r},${g},${b})`;
  }
  if (altFt >= ALTITUDE_STOPS.at(-1)!.ft) {
    const [r, g, b] = ALTITUDE_STOPS.at(-1)!.rgb;
    return `rgb(${r},${g},${b})`;
  }
  for (let i = 1; i < ALTITUDE_STOPS.length; i++) {
    const lo = ALTITUDE_STOPS[i - 1]!;
    const hi = ALTITUDE_STOPS[i]!;
    if (altFt <= hi.ft) {
      const t = (altFt - lo.ft) / (hi.ft - lo.ft);
      const r = lerp(lo.rgb[0], hi.rgb[0], t);
      const g = lerp(lo.rgb[1], hi.rgb[1], t);
      const b = lerp(lo.rgb[2], hi.rgb[2], t);
      return `rgb(${r},${g},${b})`;
    }
  }
  const [r, g, b] = ALTITUDE_STOPS.at(-1)!.rgb;
  return `rgb(${r},${g},${b})`;
}

/** MapLibre `interpolate` expression body for paint.line-color. Each stop
 * appears as `<ft>, "rgb(...)"`. Caller wraps in
 * `["interpolate", ["linear"], ["get","alt"], …]`. */
export function altitudeStopsForMapLibre(): Array<number | string> {
  const out: Array<number | string> = [];
  for (const s of ALTITUDE_STOPS) {
    out.push(s.ft, `rgb(${s.rgb[0]},${s.rgb[1]},${s.rgb[2]})`);
  }
  return out;
}
